import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Search, Eye, Trash2, FileText, FileImage, File as FileIcon } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { apiError } from "../lib/api";
import { formatDate } from "../lib/utils-format";
import { DOC_CATEGORIES } from "../config/homebuilding";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fileIcon = (ct = "") => (ct.startsWith("image/") ? FileImage : ct === "application/pdf" ? FileText : FileIcon);

function UploadDialog({ open, onOpenChange, projects, onUploaded }) {
  const [projectId, setProjectId] = useState("");
  const [category, setCategory] = useState("Plans / Drawings");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setProjectId(""); setCategory("Plans / Drawings"); setFile(null); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!projectId) { toast.error("Select a project"); return; }
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      fd.append("category", category);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onOpenChange(false); onUploaded();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Upload Document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Property / Project *</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="doc-upload-project"><SelectValue placeholder="Select a property or project…" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="doc-upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-upload-file" />
            <p className="text-xs text-muted-foreground">PDF, images, and documents up to 50 MB</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Documents() {
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = [];
    if (catFilter !== "all") q.push(`category=${encodeURIComponent(catFilter)}`);
    api.get(`/documents${q.length ? "?" + q.join("&") : ""}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [catFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([
      api.get("/projects?limit=500").catch(() => ({ data: {} })),
      api.get("/property_search?limit=500").catch(() => ({ data: {} })),
      api.get("/leads?limit=500").catch(() => ({ data: {} })),
    ]).then(([p, ps, l]) => {
      // De-dupe by name so each property/project appears once regardless of which
      // lifecycle phase it is in. Documents are grouped by name so they stay with
      // the project across every phase.
      const byName = new Map();
      const add = (id, name, priority) => {
        name = (name || "").trim();
        if (!name) return;
        const cur = byName.get(name);
        if (!cur || priority > cur.priority) byName.set(name, { id, name, priority });
      };
      (ps.data.items || []).forEach((x) => add(x.id, x.property_name || x.street_address, 1));
      (l.data.items || []).forEach((x) => add(x.id, x.property_analysis_name || `${x.first_name || ""} ${x.last_name || ""}`.trim(), 2));
      (p.data.items || []).forEach((x) => add(x.id, x.project_name || `${x.first_name || ""} ${x.last_name || ""}`.trim(), 3));
      setProjects([...byName.values()].map((v) => ({ id: v.id, name: v.name, label: v.name })));
    });
  }, []);

  const preview = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.content_type || res.data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/documents/${toDelete.id}`); toast.success("Document removed"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = items.filter((d) =>
    (projectFilter === "all" || d.project_name === projectFilter)
    && (!search || (d.original_filename || "").toLowerCase().includes(search.toLowerCase())
      || (d.project_name || "").toLowerCase().includes(search.toLowerCase())));

  const columns = [
    { key: "name", label: "Document", render: (d) => {
        const Icon = fileIcon(d.content_type);
        return <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary shrink-0" /><span className="font-medium truncate max-w-[280px]">{d.original_filename}</span></div>;
      } },
    { key: "category", label: "Category", render: (d) => <StatusBadge value={d.category} /> },
    { key: "project_name", label: "Project", render: (d) => d.project_name || "—" },
    { key: "size", label: "Size", render: (d) => fmtSize(d.size) },
    { key: "uploaded_by_name", label: "Uploaded By", render: (d) => d.uploaded_by_name || "—" },
    { key: "created_at", label: "Date", render: (d) => formatDate(d.created_at) },
    { key: "actions", label: "", render: (d) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => preview(d)} className="p-1.5 rounded hover:bg-accent" title="Open in new window" data-testid={`doc-preview-${d.id}`}><Eye className="h-3.5 w-3.5" /></button>
          <button onClick={() => setToDelete(d)} className="p-1.5 rounded hover:bg-accent text-destructive" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ) },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title="Documents" count={filtered.length}
        subtitle="Per-project file library — plans, permits, contracts, and more"
        actions={<Button onClick={() => setUploadOpen(true)} data-testid="doc-upload-btn"><Upload className="h-4 w-4 mr-1.5" />Upload</Button>}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search documents…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="doc-search" />
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[220px]" data-testid="doc-project-filter"><SelectValue placeholder="All records" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Records</SelectItem>
            {projects.map((p) => <SelectItem key={p.name} value={p.name}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[200px]" data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} items={filtered} loading={loading} emptyLabel="No documents yet — upload plans, permits, or contracts" />

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} projects={projects} onUploaded={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document?</AlertDialogTitle>
            <AlertDialogDescription>This removes “{toDelete?.original_filename}” from the project's library.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="doc-confirm-delete">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
